import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { supabase } from '../lib/supabase';
import { apiClient } from '../lib/api';
import { getSafeNextPath } from '../lib/url-utils';
import {
  readSupabaseTokensFromLocalStorage,
  setStoredAccessToken,
  stripSupabaseRefreshTokenFromStorage,
  syncSupabaseAccessTokenInStorage,
} from '../lib/supabaseAuth';

export const AuthCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function run() {
      try {
        // Support both hash (#) and query (?) style callbacks
        const url = new URL(window.location.href.replace('#', '?'));
        const errorDescription = url.searchParams.get('error_description');
        const type = url.searchParams.get('type');
        const code = url.searchParams.get('code');
        const accessToken = url.searchParams.get('access_token');
        const refreshToken = url.searchParams.get('refresh_token');
        const next = getSafeNextPath(url.searchParams.get('next'), window.location.origin);

        // SECURITY: Strip sensitive params from the address bar ASAP to avoid leaking via Referer.
        try {
          window.history.replaceState({}, document.title, '/auth/callback');
        } catch {
          // Best effort - ignore if replaceState fails
        }

        // Persist recovery intent so the main app can show the change-password UI
        if (type === 'recovery') {
          try {
            localStorage.setItem('PW_RECOVERY', '1');
          } catch {
            // Best effort - ignore if localStorage fails
          }
        }

        // Fix for XSS-VULN-01 (pentest 3 finding): error_description was reflected directly
        // from the URL into the UI, enabling phishing via attacker-crafted messages on a clean URL.
        // Now uses a generic error message instead of reflecting the URL parameter.
        if (errorDescription) {
          const safeMessages: Record<string, string> = {
            access_denied: 'Access was denied. Please try signing in again.',
            server_error: 'A server error occurred during authentication. Please try again.',
            temporarily_unavailable:
              'The authentication service is temporarily unavailable. Please try again later.',
            invalid_request: 'The authentication request was invalid. Please try signing in again.',
            unauthorized_client: 'This application is not authorized. Please contact support.',
            unsupported_response_type:
              'Authentication configuration error. Please contact support.',
          };
          // Look up known OAuth error codes; fall back to generic message — never reflect the raw URL param
          const errorCode = url.searchParams.get('error') || '';
          setError(
            safeMessages[errorCode] || 'Authentication failed. Please try signing in again.',
          );
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            setError(error.message);
            return;
          }
          const tokens = readSupabaseTokensFromLocalStorage(
            (import.meta as any).env?.VITE_SUPABASE_URL,
          );
          if (tokens.accessToken) {
            setStoredAccessToken(tokens.accessToken);
            syncSupabaseAccessTokenInStorage(
              tokens.accessToken,
              (import.meta as any).env?.VITE_SUPABASE_URL,
            );
          }
          if (tokens.refreshToken) {
            try {
              await apiClient.post(
                '/api/auth/mfa-complete',
                { refreshToken: tokens.refreshToken },
                { credentials: 'include' },
              );
            } catch {
              // Non-blocking sync
            }
          }
          // Preserve refresh token for invite/signup/recovery — SetPassword and AuthReset need it after redirect
          if (type !== 'invite' && type !== 'signup' && type !== 'recovery') {
            stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);
          }
          // Route by callback type
          if (type === 'recovery') {
            window.location.replace('/auth/reset');
          } else if (type === 'invite' || type === 'signup') {
            window.location.replace('/auth/set-password');
          } else {
            window.location.replace(next);
          }
          return;
        }

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setError(error.message);
            return;
          }
          setStoredAccessToken(accessToken);
          syncSupabaseAccessTokenInStorage(
            accessToken,
            (import.meta as any).env?.VITE_SUPABASE_URL,
          );
          try {
            await apiClient.post(
              '/api/auth/mfa-complete',
              { refreshToken },
              { credentials: 'include' },
            );
          } catch {
            // Non-blocking sync
          }
          // Preserve refresh token for invite/signup/recovery — SetPassword and AuthReset need it after redirect
          if (type !== 'invite' && type !== 'signup' && type !== 'recovery') {
            stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);
          }
          if (type === 'recovery') {
            window.location.replace('/auth/reset');
          } else if (type === 'invite' || type === 'signup') {
            window.location.replace('/auth/set-password');
          } else {
            window.location.replace(next);
          }
          return;
        }

        // If no code, redirect home
        window.location.replace('/');
      } catch (e: any) {
        setError(e?.message || 'Authentication callback failed');
      }
    }
    run();
  }, []);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      flexDirection="column"
      gap={2}
    >
      <Typography variant="h6">Signing you in…</Typography>
      {!error && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
